// src/Component/chat/component/ChatMsgSent.tsx
import { Icon } from "@iconify/react";
import { ChatContext } from "../../../context/ChatContext/chatContext";
import { ChangeEvent, FormEvent, useContext, useState, useEffect, useRef, KeyboardEvent } from "react";
import React from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { FIREBASE_COLLECTIONS } from "../../api/url";
import oneSignalConfig from "../../../config/oneSignal";

const ChatMsgSent = () => {
  const { sendMessage, sendImageMessage, sendMessageWithAttachments, selectedChat } = useContext(ChatContext);
  const [msg, setMsg] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [clientPlayerId, setClientPlayerId] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Clear input when selectedChat changes
  useEffect(() => {
    setMsg("");
    setSelectedFiles([]);
    setIsUploading(false);
    setUploadProgress("");
    setUploadError("");
  }, [selectedChat?.id]);

  // Fetch client's OneSignal playerId from Firebase when chat changes
  useEffect(() => {
    if (!selectedChat?.id) { setClientPlayerId(undefined); return; }
    const fetchPlayerId = async () => {
      try {
        const chatGroupRef = doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, selectedChat.id as string);
        const chatGroupDoc = await getDoc(chatGroupRef);
        if (!chatGroupDoc.exists()) return;
        const members: string[] = chatGroupDoc.data()?.members || [];
        const clientMemberId = members.find((id: string) => id.startsWith('userid_'));
        if (!clientMemberId) return;
        const userDocRef = doc(db, FIREBASE_COLLECTIONS.USERS, clientMemberId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setClientPlayerId(userDoc.data()?.playerId || undefined);
        }
      } catch (err) {
        console.error('Failed to fetch client playerId:', err);
      }
    };
    fetchPlayerId();
  }, [selectedChat?.id]);

  // Send OneSignal push notification to client
  const sendChatNotification = async (playerId: string, groupId: string) => {
    try {
      await fetch('https://api.onesignal.com/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_ONESIGNAL_API_KEY as string,
        },
        body: JSON.stringify({
          app_id: process.env.REACT_APP_ONESIGNAL_APP_ID,
          include_player_ids: [playerId],
          headings: { en: '💬 New Message!' },
          contents: { en: 'You\'ve got a message from your nutritionist 💬 Check now!' },
          data: { type: 'chat_message', group_id: groupId },
        }),
      });
    } catch (err) {
      console.error('Error sending chat notification:', err);
    }
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [msg]);

  const handleChatMsgChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMsg(e.target.value);
    setUploadError(""); // Clear error when user types
  };

  // Handle Enter key: Send on Enter, new line on Shift+Enter
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (msg.trim() || selectedFiles.length > 0) {
        // Trigger form submit
        const form = e.currentTarget.closest('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setSelectedFiles(Array.from(files));
      setUploadError("");
    }
  };

  const handleImageSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedChat) return;

    const imageFile = files[0];
    
    // Validate that it's an image
    if (!imageFile.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (imageFile.size > 10 * 1024 * 1024) {
      setUploadError('Image size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress('Uploading image to server...');
    setUploadError("");
    
    try {
      await sendImageMessage(selectedChat.id as string, imageFile);
      setUploadProgress('Image sent successfully!');
      if (clientPlayerId) {
        sendChatNotification(clientPlayerId, selectedChat.id as string);
      }
      setTimeout(() => {
        setUploadProgress('');
      }, 2000);
    } catch (error: any) {
      console.error('Error uploading image:', error);
      
      // Better error messages
      let errorMessage = 'Failed to send image';
      if (error?.message?.includes('fetch')) {
        errorMessage = 'Upload failed: Cannot connect to server. Please check your connection.';
      } else if (error?.message?.includes('Upload failed')) {
        errorMessage = error.message;
      } else if (error?.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      
      setUploadError(errorMessage);
      setUploadProgress('');
    } finally {
      setIsUploading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadError("");
  };

  const onChatMsgSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const trimmedMsg = msg.trim();
    
    if ((!trimmedMsg && selectedFiles.length === 0) || !selectedChat) {
      console.warn('Cannot send message - empty or no chat selected');
      return;
    }

    setIsUploading(true);
    setUploadError("");
    
    try {
      const chatId = selectedChat.id as string;
      
      if (selectedFiles.length > 0) {
        // Send message with attachments
        setUploadProgress(`Uploading ${selectedFiles.length} file(s) to server...`);
        await sendMessageWithAttachments(chatId, trimmedMsg, selectedFiles);
        setUploadProgress('Files sent successfully!');
      } else {
        // Send text-only message
        setUploadProgress('Sending message...');
        await sendMessage(chatId, trimmedMsg);
        setUploadProgress('Message sent!');
      }

      // Send OneSignal push notification to client
      if (clientPlayerId) {
        sendChatNotification(clientPlayerId, selectedChat.id as string);
      }
      
      // Clear inputs after successful send
      setTimeout(() => {
        setUploadProgress('');
      }, 1500);
      
      setMsg("");
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      // Better error messages
      let errorMessage = 'Failed to send message';
      if (error?.message?.includes('fetch')) {
        errorMessage = 'Upload failed: Cannot connect to server. Please check your connection.';
      } else if (error?.message?.includes('Upload failed')) {
        errorMessage = error.message;
      } else if (error?.message) {
        errorMessage = `Failed: ${error.message}`;
      }
      
      setUploadError(errorMessage);
      setUploadProgress('');
    } finally {
      setIsUploading(false);
    }
  };

  if (!selectedChat) {
    return null;
  }

  // Determine if send button should be enabled
  const canSend = !isUploading && (msg.trim() || selectedFiles.length > 0);

  return (
    <div className="border-t border-ld bg-white">
      <form onSubmit={onChatMsgSubmit}>
        {/* Error Display */}
        {uploadError && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-200">
            <div className="flex items-start gap-3 text-sm text-red-700">
              <Icon icon="solar:danger-triangle-outline" height={20} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium mb-1">Upload Error</p>
                <p className="text-xs leading-relaxed">{uploadError}</p>
                {uploadError.includes('server') && (
                  <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                    <p className="font-medium mb-1">💡 Possible solutions:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-red-600">
                      <li>Check if the backend server is running</li>
                      <li>Verify the API endpoint: <code className="bg-red-200 px-1 rounded">{window.location.origin}/upload_file/</code></li>
                      <li>Check your internet connection</li>
                    </ul>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setUploadError("")}
                className="text-red-500 hover:text-red-700 transition-colors"
              >
                <Icon icon="solar:close-circle-outline" height={18} />
              </button>
            </div>
          </div>
        )}

        {/* File Preview Section */}
        {selectedFiles.length > 0 && (
          <div className="px-5 pt-3 pb-2 border-b border-ld bg-gray-50">
            <p className="text-xs text-gray-600 mb-2 font-medium">
              {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm shadow-sm hover:shadow-md transition-shadow"
                >
                  <Icon 
                    icon={file.type.startsWith('image/') ? "solar:gallery-bold" : "solar:document-bold"} 
                    height={20} 
                    className={file.type.startsWith('image/') ? "text-blue-500" : "text-gray-500"}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate max-w-[120px]" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full p-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isUploading}
                    title="Remove file"
                  >
                    <Icon icon="solar:close-circle-bold" height={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Section */}
        <div className="flex gap-3 items-end py-4 px-5">
          {/* Emoji Button (placeholder) */}
          <button
            type="button"
            className="btn-circle-hover cursor-pointer flex items-center justify-center h-10 w-10 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-full transition-colors mb-1"
            disabled={isUploading}
            title="Emoji (coming soon)"
          >
            <Icon icon="solar:sticker-smile-circle-2-linear" height={22} className="text-gray-600" />
          </button>

          {/* Text Input - Now a textarea */}
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              className="w-full px-4 py-3 border-0 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed resize-none overflow-y-auto"
              placeholder={isUploading ? "Uploading..." : "Type a message (Shift+Enter for new line)"}
              value={msg}
              onChange={handleChatMsgChange}
              onKeyDown={handleKeyDown}
              disabled={isUploading}
              rows={1}
              style={{ minHeight: '44px', maxHeight: '150px' }}
            />
          </div>

          <div className="flex gap-2 items-center mb-1">
            {/* Send Button - Always visible */}
            <button
              type="submit"
              disabled={!canSend}
              className={`flex items-center justify-center h-12 w-12 rounded-full transition-all duration-200 ${
                canSend 
                  ? 'text-[#1f1f1f] cursor-pointer ' 
                  : 'text-gray-300  cursor-not-allowed opacity-50'
              }`}
              title={!canSend ? "Type a message or attach a file to send" : "Send message"}
            >
              {isUploading ? (
                <Icon icon="solar:loading-outline" height={26} className="animate-spin" />
              ) : (
                <Icon icon="solar:plain-bold" height={26} />
              )}
            </button>

            {/* Image Upload */}
            <button
              type="button"
              className="btn-circle-hover cursor-pointer flex items-center justify-center h-12 w-12 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => imageInputRef.current?.click()}
              disabled={isUploading}
              title="Send image"
            >
              <Icon icon="solar:gallery-add-bold" height={26} className="text-gray-600" />
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              disabled={isUploading}
            />

            {/* File Attachment */}
            {/* <button
              type="button"
              className="btn-circle-hover cursor-pointer flex items-center justify-center h-10 w-10 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              title="Attach files"
            >
              <Icon icon="solar:paperclip-bold" height={22} className="text-gray-600" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isUploading}
            /> */}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatMsgSent;